import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Edit2,
  Link2,
  Unlink,
  Search,
  Plus,
  Check,
  Save,
  X,
  CircleCheck,
  CircleMinus,
  ChevronDown,
  Lock,
} from "lucide-react";
import {
  governancePoliciesService,
  governanceFrameworksService,
  policyProcedureLinksService,
  GovernancePolicy,
  GovernanceFramework,
  PolicyProcedureLink,
  AvailableProcedure,
} from "../../services/governanceService";
import FormControl from "../common/FormControl";
import Modal from "../common/Modal";
import Button from "../common/Button";
import Skeleton from "../common/Skeleton";
import Pagination from "../common/Pagination";
import Toast, { ToastMessage } from "../common/Toast";
import NoDataFound from "../common/NoDataFound";
import { confirmWarning, confirmActive } from "../common/confirm";
import GovernanceProcesses from "./mock/GovernanceProcesses";
import WorkflowModal from "../common/WorkflowModal";
import { usePermissions, NOT_AUTHORISED_MSG } from "../../contexts/PermissionContext";
import { useFieldValidation } from "../../validation/useFieldValidation";
import { ValidatedInput, ValidatedTextarea, ValidatedDatePicker } from "../../validation/ValidatedFields";
import { isUniqueLocal } from "../../validation/uniqueness";
import { FieldSpec } from "../../validation/engine";

// Regulations share their parent framework's remit — one grant covers a
// framework and the regulations underneath it. GOV_SOPS governs SOPs only;
// linking a regulation to an SOP is allowed to holders of either.
const ACT_POLICIES = "GOV_FRAMEWORKS";
const ACT_SOPS = "GOV_SOPS";



interface GovernancePoliciesModalProps {
  frameworkId: number;
  frameworkName: string;
  framework?: GovernanceFramework;
  onClose: () => void;
  onEditFramework?: () => void;
  onFrameworkUpdated?: () => void;
}

const WORKFLOW_STATUS_OPTIONS = [
  { value: "ALL", label: "All Statuses" },
  { value: "draft", label: "Draft" },
  { value: "pending_approval", label: "Pending Approval" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const GovernancePoliciesModal: React.FC<GovernancePoliciesModalProps> = ({
  frameworkId,
  frameworkName,
  framework,
  onClose,
  onEditFramework,
  onFrameworkUpdated,
}) => {
  const [policies, setPolicies] = useState<GovernancePolicy[]>([]);
  const [frameworks, setFrameworks] = useState<GovernanceFramework[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [workflowFilter, setWorkflowFilter] = useState("ALL");
  const [showInactive, setShowInactive] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState<GovernancePolicy | null>(
    null,
  );
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  // Policy whose status dot was clicked — opens the shared workflow modal.
  const [workflowPolicy, setWorkflowPolicy] = useState<GovernancePolicy | null>(null);
  const { check } = usePermissions();
  // Regulation CRUD follows the framework remit; SOP linking accepts either
  // remit, mirroring the backend gate on /policies/{id}/sops.
  const canModifyPolicy = check(ACT_POLICIES, "view+modify").allowed;
  const canLinkSop =
    canModifyPolicy || check(ACT_SOPS, "view+modify").allowed;
  // Approved records are frozen: no edit, no deactivate. Unlock via the status
  // dot's Reopen action. Frameworks use workflow_state; policies workflow_status.
  const fwApproved = (framework as any)?.workflow_state === "approved";
  const policyApproved = selectedPolicy?.workflow_status === "approved";
  const [formData, setFormData] = useState<Partial<GovernancePolicy>>({});
  const [activeTab, setActiveTab] = useState<"policies" | "details" | "edit">(
    "edit",
  );

  const [fwFormData, setFwFormData] = useState<Partial<GovernanceFramework>>({});
  const [fwSubmitting, setFwSubmitting] = useState(false);
  const [fwToggling, setFwToggling] = useState(false);
  const [isFwEditMode, setIsFwEditMode] = useState(false);
  const fwFormRef = useRef<HTMLFormElement>(null);

  const [linkedProcedures, setLinkedProcedures] = useState<
    PolicyProcedureLink[]
  >([]);
  const [availableProcedures, setAvailableProcedures] = useState<
    AvailableProcedure[]
  >([]);
  const [showLinkPanel, setShowLinkPanel] = useState(false);
  const [linkSearch, setLinkSearch] = useState("");
  const [linksLoading, setLinksLoading] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [sopProfileId, setSopProfileId] = useState<number | null>(null);

  // Inline-validation handlers — one per sub-form.
  const policyVh = useFieldValidation();
  const fwVh = useFieldValidation();

  useEffect(() => {
    setSubmitting(false);
    setToggling(false);
  }, [selectedPolicy, showPolicyModal]);

  const loadFrameworks = useCallback(async () => {
    try {
      const data = await governanceFrameworksService.getAll({
        status: "ACTIVE",
      });
      setFrameworks(data);
    } catch {
      // non-critical
    }
  }, []);

  const loadPolicies = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {
        search: searchTerm || undefined,
        framework_id: frameworkId,
      };
      if (workflowFilter !== "ALL") params.workflow_status = workflowFilter;
      if (!showInactive) params.status = "ACTIVE";
      const data = await governancePoliciesService.getAll(params);
      setPolicies(data);
    } catch (err: any) {
      setToast({ type: "error", message: err.message || "Failed to load policies" });
    } finally {
      setLoading(false);
    }
  }, [frameworkId, searchTerm, workflowFilter, showInactive]);

  useEffect(() => {
    loadFrameworks();
  }, [loadFrameworks]);
  useEffect(() => {
    loadPolicies();
  }, [loadPolicies]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, workflowFilter, showInactive, pageSize]);

  const loadLinkedProcedures = useCallback(async (policyId: number) => {
    try {
      setLinksLoading(true);
      const links =
        await policyProcedureLinksService.getLinksForPolicy(policyId);
      setLinkedProcedures(links);
    } catch {
      setLinkedProcedures([]);
    } finally {
      setLinksLoading(false);
    }
  }, []);

  const loadAvailableProcedures = useCallback(
    async (policyId: number, search?: string) => {
      try {
        const available =
          await policyProcedureLinksService.getAvailableProcedures(
            policyId,
            search,
          );
        setAvailableProcedures(available);
      } catch (err: any) {
        setAvailableProcedures([]);
        const detail =
          err?.response?.data?.detail ||
          err?.data?.detail ||
          err?.message ||
          "Failed to load available SOPs";
        setToast({ type: "error", message: typeof detail === "string" ? detail : JSON.stringify(detail) });
        // eslint-disable-next-line no-console
        console.error("loadAvailableProcedures failed:", err);
      }
    },
    [],
  );

  const handleLinkProcedure = async (proc: AvailableProcedure) => {
    if (!selectedPolicy) return;
    try {
      await policyProcedureLinksService.createLink({
        policy_id: selectedPolicy.id,
        sop_id: proc.id,
      });
      await loadLinkedProcedures(selectedPolicy.id);
      await loadAvailableProcedures(selectedPolicy.id, linkSearch || undefined);
    } catch (err: any) {
      setToast({ type: "error", message: err.message || "Failed to link SOP" });
    }
  };

  const handleUnlinkProcedure = async (sopId: number) => {
    if (!selectedPolicy) return;
    try {
      await policyProcedureLinksService.removeLink(selectedPolicy.id, sopId);
      await loadLinkedProcedures(selectedPolicy.id);
      if (showLinkPanel) {
        await loadAvailableProcedures(
          selectedPolicy.id,
          linkSearch || undefined,
        );
      }
    } catch (err: any) {
      setToast({ type: "error", message: err.message || "Failed to unlink SOP" });
    }
  };

  const handleRowClick = (policy: GovernancePolicy) => {
    if (selectedPolicy?.id === policy.id) {
      setSelectedPolicy(null);
      setLinkedProcedures([]);
      setShowLinkPanel(false);
    } else {
      setSelectedPolicy(policy);
      loadLinkedProcedures(policy.id);
      setShowLinkPanel(false);
    }
  };

  const handleEditPolicy = (e: React.MouseEvent, policy: GovernancePolicy) => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedPolicy(policy);
    setFormData(policy);
    policyVh.reset();
    setIsEditMode(false);
    setShowPolicyModal(true);
  };

  const handleAddNew = () => {
    setSelectedPolicy(null);
    setFormData({
      policy_code: "",
      policy_name: "",
      policy_description: "",
      framework_id: frameworkId,
      version_number: "1.0",
      workflow_status: "draft",
      is_active: true,
      start_date: new Date().toISOString().split("T")[0],
      display_order: 999,
    });
    policyVh.reset();
    setIsEditMode(false);
    setShowPolicyModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.framework_id) {
      setToast({ type: "error", message: "Reg Framework is required." });
      return;
    }
    if (!formData.version_number?.trim()) {
      setToast({ type: "error", message: "Version is required." });
      return;
    }

    // Engine-level format validation (109 name / 110 description / 305 dates)
    // plus the 601 cross-field (start ≤ end) when both dates are present.
    const specs: FieldSpec[] = [
      { name: "policy_name", code: 109, value: formData.policy_name || "", required: true },
      { name: "policy_description", code: 110, value: formData.policy_description || "" },
      { name: "start_date", code: 305, value: formData.start_date || "", required: true },
      { name: "end_date", code: 305, value: formData.end_date || "" },
    ];
    if (formData.start_date && formData.end_date) {
      specs.push({
        name: "end_date", code: 601, value: "x", required: true,
        additional: { from_date: formData.start_date, to_date: formData.end_date },
      });
    }
    const formatOk = policyVh.validateAll(specs);

    // Local uniqueness pre-check on policy_name (code 947, entity-scoped).
    let uniqueOk = true;
    if (!isUniqueLocal(947, formData.policy_name || "", policies, isEditMode ? selectedPolicy?.id : undefined)) {
      policyVh.setError("policy_name", "A regulation with this name already exists");
      uniqueOk = false;
    }

    if (!formatOk || !uniqueOk) {
      setToast({ type: "error", message: "Please fix the highlighted fields" });
      return;
    }
    setSubmitting(true);
    try {
      if (isEditMode && selectedPolicy) {
        const payload: any = { ...formData };
        delete payload.is_active;
        await governancePoliciesService.update(selectedPolicy.id, payload);
        setToast({ type: "success", message: "Regulation updated successfully" });
        setIsEditMode(false);
      } else {
        await governancePoliciesService.create(formData);
        setToast({ type: "success", message: "Regulation created successfully" });
        setShowPolicyModal(false);
      }
      loadPolicies();
    } catch (err: any) {
      setToast({
        type: "error",
        message: err.message || "Failed to save policy",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async () => {
    if (!selectedPolicy) return;
    const next = selectedPolicy.is_active ? false : true;
    const confirmFn = selectedPolicy.is_active ? confirmWarning : confirmActive;
    const ok = await confirmFn({
      title: next ? "Reactivate this policy?" : "Inactivate this policy?",
      text: next
        ? "It will appear in active policy lists again."
        : "It will be hidden from active policy lists.",
      confirmText: next ? "Yes, Reactivate" : "Yes, Inactivate",
    });
    if (!ok) return;
    setToggling(true);
    try {
      await governancePoliciesService.update(selectedPolicy.id, {
        is_active: next,
      });
      const updated = { ...selectedPolicy, is_active: next };
      setSelectedPolicy(updated);
      setFormData((prev) => ({ ...prev, is_active: next }));
      setToast({
        type: next ? "success" : "warning",
        message: next ? "Reactivated." : "Inactivated.",
      });
      loadPolicies();
    } catch (err: any) {
      setToast({
        type: "error",
        message: err.message || "Failed to update status",
      });
    } finally {
      setToggling(false);
    }
  };

  const handleSubmitForApproval = async () => {
    if (!selectedPolicy) return;
    try {
      await governancePoliciesService.submitForApproval(selectedPolicy.id);
      setShowPolicyModal(false);
      loadPolicies();
    } catch (err: any) {
      setToast({ type: "error", message: err.message || "Failed to submit policy for approval" });
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : name === "framework_id"
            ? parseInt(value)
            : value,
    }));
  };

  const handleCheckboxChange = (name: string, checked: boolean) => {
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  useEffect(() => {
    if (activeTab === "edit" && framework) {
      setFwFormData({
        ...framework,
        framework_description: (framework as any).description || framework.framework_description || "",
        start_date: (framework as any).applicability_date || framework.start_date || "",
        end_date: (framework as any).end_date || "",
      });
      fwVh.reset();
      setIsFwEditMode(false);
    }
  }, [activeTab, framework]);

  const handleFwInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFwFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (name === "is_active" ? (checked ? 1 : 0) : checked) : value,
    }));
  };

  const handleFwCheckboxChange = (name: string, checked: boolean) => {
    setFwFormData((prev) => ({
      ...prev,
      [name]: name === "is_active" ? (checked ? 1 : 0) : checked,
    }));
  };

  const handleFrameworkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Engine-level format validation (109 name / 110 description / 305 date).
    const formatOk = fwVh.validateAll([
      { name: "framework_name", code: 109, value: fwFormData.framework_name || "", required: true },
      { name: "framework_description", code: 110, value: fwFormData.framework_description || "" },
      { name: "start_date", code: 305, value: fwFormData.start_date || "", required: true },
    ]);

    // Local uniqueness pre-check on framework_name (code 946, entity-scoped).
    let uniqueOk = true;
    if (!isUniqueLocal(946, fwFormData.framework_name || "", frameworks, frameworkId)) {
      fwVh.setError("framework_name", "Framework with this name already exists");
      uniqueOk = false;
    }

    if (!formatOk || !uniqueOk) {
      setToast({ type: "error", message: "Please fix the highlighted fields" });
      return;
    }
    setFwSubmitting(true);
    try {
      const payload: any = {
        ...fwFormData,
        applicability_date: fwFormData.start_date || undefined,
        end_date: fwFormData.end_date || null,
        description: fwFormData.framework_description || undefined,
      };
      delete payload.start_date;
      delete payload.display_order;
      delete payload.framework_description;
      delete payload.is_active;
      await governanceFrameworksService.update(frameworkId, payload);
      setToast({ type: "success", message: "Framework updated successfully" });
      setIsFwEditMode(false);
      onFrameworkUpdated?.();
    } catch (err: any) {
      setToast({ type: "error", message: err.message || "Failed to update framework" });
    } finally {
      setFwSubmitting(false);
    }
  };

  const handleFwToggleActive = async () => {
    const next = fwFormData.is_active ? 0 : 1;
    const isActive = fwFormData.is_active;
    const confirmFn = isActive ? confirmWarning : confirmActive;
    const ok = await confirmFn({
      title: next ? "Reactivate this framework?" : "Inactivate this framework?",
      text: next
        ? "It will appear in active framework lists again."
        : "It will be hidden from active framework lists.",
      confirmText: next ? "Yes, Reactivate" : "Yes, Inactivate",
    });
    if (!ok) return;
    setFwToggling(true);
    try {
      await governanceFrameworksService.update(frameworkId, { is_active: next });
      setFwFormData((prev) => ({ ...prev, is_active: next }));
      setToast({
        type: next ? "success" : "warning",
        message: next ? "Framework reactivated" : "Framework inactivated",
      });
      setIsFwEditMode(false);
      onFrameworkUpdated?.();
    } catch (err: any) {
      setToast({ type: "error", message: err.message || "Failed to update status" });
    } finally {
      setFwToggling(false);
    }
  };


  // Only approved + active frameworks belong in the policy's framework picker.
  // `frameworks` also backs the framework-management/uniqueness checks, so it
  // intentionally holds all states — filter to approved here at the picker.
  // Gate on the maker-checker workflow_state (same as /frameworks/dropdown); the
  // legacy is_approved flag is not set by the approve transition, so filtering on
  // it wrongly hid approved frameworks from the Reg Framework picker.
  const frameworkOptions = frameworks
    .filter((fw) => (fw.is_active ?? 1) === 1 && fw.workflow_state === 'approved')
    .map((fw) => ({
      value: String(fw.id),
      label: fw.framework_name,
    }));

  // The Reg Framework field is LOCKED to the parent framework this modal was
  // opened from (see `disabled={!!frameworkId ...}` below) and formData.framework_id
  // is pre-set to it. That parent may be draft/submitted (not in the approved
  // list above), so inject it explicitly — otherwise SearchSelect finds no option
  // matching its value and renders an empty "Select…" instead of the parent name.
  if (frameworkId && !frameworkOptions.some((o) => o.value === String(frameworkId))) {
    frameworkOptions.unshift({
      value: String(frameworkId),
      label: frameworkName || framework?.framework_name || `Framework ${frameworkId}`,
    });
  }

  const totalRecords = policies.length;
  const paginatedPolicies = policies.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );

  return (
    <>
      {/* ── Outer modal: policies viewer ── */}
      <Modal isOpen onClose={onClose} size="lg" closeOnOverlayClick={false}>
        <Modal.Header>
          {fwApproved && activeTab === "edit" ? (
            <>Viewing (locked) "<span className="item-highlight">{frameworkName}</span>"</>
          ) : (
            <>Regulations — {frameworkName}</>
          )}
        </Modal.Header>

        <Modal.Body>
          {/* Tabs */}
          <div className="app-modal-tabs">
            {framework && (
              <button
                className={`app-modal-tab${activeTab === "edit" ? " app-modal-tab--active" : ""}`}
                onClick={() => setActiveTab("edit")}
                data-testid="TAB_policy_basic"
              >
                Basic Details
              </button>
            )}
            <button
              className={`app-modal-tab${activeTab === "policies" ? " app-modal-tab--active" : ""}`}
              onClick={() => setActiveTab("policies")}
              data-testid="TAB_policy_policies"
            >
              Regulations
            </button>
            <button
              className={`app-modal-tab${activeTab === "details" ? " app-modal-tab--active" : ""}`}
              onClick={() => setActiveTab("details")}
              data-testid="TAB_policy_details"
            >
              Additional Details
            </button>
          </div>

          {/* ── Policies tab ── */}
          {activeTab === "policies" && (
            <>
              <div className="filter-area flex flex-wrap gap-4 items-center justify-between">
                <div className="flex items-center gap-2 ml-auto">
                  <FormControl.SearchSelect
                    value={workflowFilter}
                    onChange={(v) => setWorkflowFilter(String(v ?? "ALL"))}
                    className="filter-status"
                    noSearchIcon
                    data-testid="SEL_policy_workflow_filter"
                    options={WORKFLOW_STATUS_OPTIONS}
                  />
                  <FormControl.SearchSelect
                    value={showInactive ? "ALL" : "ACTIVE"}
                    onChange={(v) => setShowInactive(v === "ALL")}
                    className="filter-status"
                    noSearchIcon
                    data-testid="SEL_policy_status_filter"
                    options={[
                      { value: "ACTIVE", label: "Active Only" },
                      { value: "ALL", label: "All (Including Inactive)" },
                    ]}
                  />
                  <FormControl.Input
                    type="text"
                    placeholder="Search regulations…"
                    value={searchTerm}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setSearchTerm(e.target.value)
                    }
                    leadingIcon={<Search size={14} />}
                    className="filter-search"
                    data-testid="INP_policy_search"
                  />
                  <Button
                    // variant="secondary-outline"
                    icon={Plus}
                    onClick={handleAddNew}
                    className="btn-primary-fill"
                    disabled={!canModifyPolicy}
                    title={!canModifyPolicy ? NOT_AUTHORISED_MSG : undefined}
                    data-testid="BTN_policy_create"
                  >
                    Create
                  </Button>
                </div>
              </div>

              <div
                className="collapse-section-stack"
                style={{ marginTop: 8 }}
                aria-busy={loading}
                aria-live="polite"
              >
                {loading &&
                  [0, 1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="collapse-section collapse-section--l1 cs-closed"
                    >
                      <div
                        className="collapse-section__header"
                        style={{ pointerEvents: "none" }}
                      >
                        <span className="collapse-section__toggle">
                          <ChevronDown size={14} />
                        </span>
                        <div className="collapse-section__title-block">
                          <Skeleton.Box height={11} width={60} />
                          <Skeleton.Box height={13} width={220} />
                        </div>
                        <div className="collapse-section__meta">
                          <Skeleton.Box height={18} width={80} radius={999} />
                          <Skeleton.Box height={18} width={60} radius={999} />
                        </div>
                      </div>
                    </div>
                  ))}

                {!loading && policies.length === 0 && (
                  <NoDataFound
                    title="No regulations found"
                    message="No regulations match the current filters."
                  />
                )}

                {!loading &&
                  paginatedPolicies.map((policy, idx) => {
                    const expanded = selectedPolicy?.id === policy.id;
                    return (
                      <div
                        key={policy.id}
                        className={`collapse-section collapse-section--l1 ${expanded ? "cs-open" : "cs-closed"}`}
                      >
                        <div
                          className="collapse-section__header"
                          onClick={() => handleRowClick(policy)}
                          data-testid={`BTN_policy_row_${policy.id}_toggle`}
                        >
                          <span className="collapse-section__toggle">
                            <ChevronDown size={14} />
                          </span>
                          <div className="collapse-section__title-block">
                            <span className="collapse-section__code">
                              {(page - 1) * pageSize + idx + 1}. {policy.policy_code} · v{policy.version_number}
                            </span>
                            <span className="collapse-section__name">
                              {policy.policy_name}
                            </span>
                          </div>
                          <div className="collapse-section__meta">
                            <span
                              className={`status-badge sm ${policy.is_active ? "success" : "neutral"}`}
                            >
                              {policy.is_active ? "Active" : "Inactive"}
                            </span>
                            <button
                              onClick={(e) => handleEditPolicy(e, policy)}
                              className="table-list-action table-list-action--edit"
                              disabled={!canModifyPolicy}
                              title={!canModifyPolicy ? NOT_AUTHORISED_MSG : "Edit Regulation"}
                              data-testid={`BTN_policy_row_${policy.id}_edit`}
                            >
                              <Edit2 size={14} />
                            </button>
                          </div>
                        </div>

                        {expanded && (
                          <div className="collapse-section__body">
                            {policy.policy_description && (
                              <p className="collapse-section__desc" style={{ margin: 0 }}>
                                {policy.policy_description}
                              </p>
                            )}

                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                              }}
                            >
                              <Button
                                variant="secondary-outline"
                                icon={Link2}
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation();
                                  setLinkSearch("");
                                  loadAvailableProcedures(policy.id);
                                  setShowLinkPanel(true);
                                }}
                                disabled={!canLinkSop}
                                title={!canLinkSop ? NOT_AUTHORISED_MSG : undefined}
                                data-testid={`BTN_policy_row_${policy.id}_link_sop`}
                              >
                                + Link SOP
                              </Button>
                            </div>

                            {linksLoading ? (
                              <div className="collapse-section__skeleton">
                                <Skeleton.Box height={12} width="60%" />
                                <Skeleton.Box height={12} width="80%" />
                                <Skeleton.Box height={12} width="45%" />
                              </div>
                            ) : linkedProcedures.length === 0 ? (
                              <p className="collapse-section__empty">
                                No SOPs linked. Click "+ Link SOP" to add.
                              </p>
                            ) : (
                              <div className="collapse-section__nested">
                                {linkedProcedures.map((link) => (
                                  <div
                                    key={link.id}
                                    className="collapse-card"
                                    role="button"
                                    tabIndex={0}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSopProfileId(link.sop_id);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        setSopProfileId(link.sop_id);
                                      }
                                    }}
                                    title="Open SOP profile"
                                  >
                                    <div className="collapse-card__row">
                                      <span className="collapse-card__title">
                                        {link.sop_name}
                                        {link.sop_description && (
                                          <span style={{ color: "#6b7280", fontWeight: 400, marginLeft: 6 }}>
                                            — {link.sop_description}
                                          </span>
                                        )}
                                      </span>
                                      <div className="collapse-card__meta">
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleUnlinkProcedure(link.sop_id);
                                          }}
                                          className="btn-danger-outline-none"
                                          disabled={!canLinkSop}
                                          title={!canLinkSop ? NOT_AUTHORISED_MSG : "Unlink"}
                                          data-testid={`BTN_policy_sop_${link.sop_id}_unlink`}
                                        >
                                          <Unlink size={10} />
                                        </button>
                                      </div>
                                    </div>
                                    <div className="collapse-card__badges">
                                      {link.sop_code && (
                                        <span className="status-badge sm info">
                                          {link.sop_code}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
                <Pagination
                  page={page}
                  pageSize={pageSize}
                  totalRecords={totalRecords}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                  recordLabel="regulations"
                />
            </>
          )}

          {/* ── Edit Framework tab ── */}
          {activeTab === "edit" && fwApproved && (
            <div className="approved-banner" data-testid="BANNER_framework_locked">
              <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
            </div>
          )}
          {activeTab === "edit" && (
            <form
              ref={fwFormRef}
              onSubmit={handleFrameworkSubmit}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                columnGap: '1.5rem',
                rowGap: '1rem',
                width: '100%',
                boxSizing: 'border-box',
                paddingTop: '1rem'
              }}
              data-testid="MOD_policy_framework_edit_form"
            >
              {/* System code (shown in the list) — read-only, never editable. */}
              <div style={{ minWidth: 0 }}>
                <FormControl.Input
                  label="Code"
                  value={(fwFormData as any).code || fwFormData.framework_code || "—"}
                  disabled
                />
              </div>
              <div style={{ minWidth: 0 }}>
                <ValidatedInput
                  vh={fwVh}
                  code={109}
                  required
                  label="Framework Name"
                  name="framework_name"
                  value={fwFormData.framework_name || ""}
                  onChange={handleFwInputChange}
                  onAfterBlur={(v) => {
                    if (!isUniqueLocal(946, v, frameworks, frameworkId)) {
                      fwVh.setError("framework_name", "Framework with this name already exists");
                    }
                  }}
                  placeholder="e.g., GDPR, ISO 27001"
                  disabled={!isFwEditMode}
                />
              </div>
              <div style={{ minWidth: 0 }}>
                <ValidatedDatePicker
                  vh={fwVh}
                  name="start_date"
                  code={305}
                  required
                  label="Effective From Date"
                  value={fwFormData.start_date || ""}
                  onChange={(e) =>
                    setFwFormData((prev) => ({ ...prev, start_date: e.target.value }))
                  }
                  disabled={!isFwEditMode}
                />
              </div>
              <div style={{ gridColumn: 'span 3', minWidth: 0 }}>
                <ValidatedTextarea
                  vh={fwVh}
                  code={110}
                  label="Description"
                  name="framework_description"
                  value={fwFormData.framework_description || ""}
                  onChange={handleFwInputChange}
                  rows={3}
                  placeholder="Brief description of the framework"
                  disabled={!isFwEditMode}
                />
              </div>
              <div style={{ minWidth: 0 }}>
                <FormControl.Checkbox
                  label="Is Global Framework"
                  checked={fwFormData.is_global || false}
                  onChange={(checked) => handleFwCheckboxChange("is_global", checked)}
                  disabled={!isFwEditMode}
                />
              </div>
            </form>
          )}

          {/* ── Additional Details tab ── */}
          {activeTab === "details" && (
            <div className="p-2">
              <h3 className="text-base font-semibold text-gray-800 mb-3">
                Framework Details
              </h3>
              <dl className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                {[
                  { label: "Framework Name", value: frameworkName },
                  { label: "Total Regulations", value: policies.length },
                  {
                    label: "Active Regulations",
                    value: policies.filter((p) => p.is_active).length,
                  },
                  {
                    label: "Approved Regulations",
                    value: policies.filter(
                      (p) => p.workflow_status === "approved",
                    ).length,
                  },
                  {
                    label: "Pending Approval",
                    value: policies.filter(
                      (p) => p.workflow_status === "pending_approval",
                    ).length,
                  },
                ].map(({ label, value }) => (
                  <div key={label} className="flex gap-2">
                    <dt className="font-medium text-gray-600">{label}:</dt>
                    <dd className="text-gray-800">{value}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500 leading-relaxed">
                  This section can be expanded to show additional
                  framework-level information, analytics, compliance status, or
                  related documentation links.
                </p>
              </div>
            </div>
          )}
        </Modal.Body>
        {activeTab === "edit" && !fwApproved && (
          <Modal.Footer className="app-modal-footer--split">
            <Button
              variant={fwFormData.is_active ? "inactive-outline" : "active-outline"}
              icon={fwFormData.is_active ? CircleMinus : CircleCheck}
              onClick={handleFwToggleActive}
              disabled={fwToggling || fwSubmitting}
              data-testid="BTN_policy_framework_toggle_active"
            >
              {fwToggling ? "..." : fwFormData.is_active ? "Inactive" : "Active"}
            </Button>
            {isFwEditMode ? (
              <Button
                variant="update"
                icon={Check}
                disabled={fwSubmitting || fwToggling}
                onClick={() => fwFormRef.current?.requestSubmit()}
                data-testid="BTN_policy_framework_update"
              >
                {fwSubmitting ? "Updating..." : "Update"}
              </Button>
            ) : (
              <Button
                variant="fill"
                icon={Edit2}
                onClick={() => setIsFwEditMode(true)}
                data-testid="BTN_policy_framework_edit"
              >
                Edit
              </Button>
            )}
          </Modal.Footer>
        )}
      </Modal>

      {/* ── Edit / Create Policy modal ── */}
      <Modal
        isOpen={showPolicyModal}
        onClose={() => setShowPolicyModal(false)}
        size="md"
      >
        <Modal.Header>
          {selectedPolicy ? (
            policyApproved ? (
              <>
                Viewing (locked) "
                <span className="item-highlight">
                  {selectedPolicy.policy_name}
                </span>
                "
              </>
            ) : isEditMode ? (
              <>
                Editing "
                <span className="item-highlight">
                  {selectedPolicy.policy_name}
                </span>
                "
              </>
            ) : (
              <>
                Viewing "
                <span className="item-highlight">
                  {selectedPolicy.policy_name}
                </span>
                "
              </>
            )
          ) : (
            "Create Regulation"
          )}
        </Modal.Header>
        <Modal.Body>
          {isEditMode && policyApproved && (
            <div className="approved-banner" data-testid="BANNER_policy_locked">
              <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
            </div>
          )}
          <form
            id="policy-form"
            onSubmit={handleSubmit}
            className="grid grid-cols-2 gap-x-6 gap-y-4"
            data-testid="MOD_policy_form"
          >
            {isEditMode && policyApproved && (
              <div className="approved-banner col-span-2" data-testid="BANNER_policy_locked_form">
                <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
              </div>
            )}
            {/* Code — in edit mode show the system code (read-only);
                on create it is auto-generated (not shown). */}
            {isEditMode && (
              <FormControl.Input
                label="Code"
                value={(selectedPolicy as any)?.code || selectedPolicy?.policy_code || "—"}
                disabled
                data-testid="INP_policy_code"
              />
            )}
            <FormControl.SearchSelect
              label="Reg Framework"
              required
              options={frameworkOptions}
              value={String(formData.framework_id || "")}
              onChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  framework_id:
                    value != null ? parseInt(String(value)) : undefined,
                }))
              }
              disabled={!!(!!frameworkId || (selectedPolicy && !isEditMode))}
              data-testid="SEL_policy_framework"
            />
            <FormControl.Input
              label="Regulation Name"
              required
              name="policy_name"
              className="col-span-2"
              value={formData.policy_name || ""}
              onChange={handleInputChange}
              placeholder="e.g., Data Protection Policy"
              disabled={!!(selectedPolicy && !isEditMode)}
              data-testid="INP_policy_name"
              error={policyVh.errors.policy_name}
              onBlur={(e) => {
                policyVh.validateField("policy_name", 109, e.target.value, { required: true });
                if (!isUniqueLocal(947, e.target.value, policies, isEditMode ? selectedPolicy?.id : undefined)) {
                  policyVh.setError("policy_name", "A regulation with this name already exists");
                }
              }}
            />
            <FormControl.Input
              label="Version"
              required
              name="version_number"
              value={formData.version_number || "1.0"}
              onChange={handleInputChange}
              placeholder="e.g., 1.0"
              disabled={!!(selectedPolicy && !isEditMode)}
              data-testid="INP_policy_version"
            />
            <ValidatedDatePicker
              vh={policyVh}
              name="start_date"
              code={305}
              required
              label="Start Date"
              value={formData.start_date || ""}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, start_date: e.target.value }))
              }
              disabled={!!(selectedPolicy && !isEditMode)}
              onAfterBlur={() => {
                if (formData.start_date && formData.end_date &&
                    new Date(formData.start_date) > new Date(formData.end_date)) {
                  policyVh.setError("end_date", "From date must be on or before the to date");
                }
              }}
              data-testid="INP_policy_start_date"
            />
            <ValidatedDatePicker
              vh={policyVh}
              name="end_date"
              code={305}
              label="End Date"
              value={formData.end_date || ""}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, end_date: e.target.value }))
              }
              disabled={!!(selectedPolicy && !isEditMode)}
              onAfterBlur={(v) => {
                if (formData.start_date && v &&
                    new Date(formData.start_date) > new Date(v)) {
                  policyVh.setError("end_date", "From date must be on or before the to date");
                }
              }}
              data-testid="INP_policy_end_date"
            />
            <ValidatedTextarea
              vh={policyVh}
              code={110}
              label="Description"
              name="policy_description"
              className="col-span-2"
              value={formData.policy_description || ""}
              onChange={handleInputChange}
              rows={4}
              placeholder="Detailed policy description"
              disabled={!!(selectedPolicy && !isEditMode)}
              data-testid="INP_policy_description"
            />
          </form>
        </Modal.Body>
        {selectedPolicy && !policyApproved ? (
          <Modal.Footer className="app-modal-footer--split">
            <Button
              variant={
                selectedPolicy?.is_active
                  ? "inactive-outline"
                  : "active-outline"
              }
              icon={selectedPolicy?.is_active ? CircleMinus : CircleCheck}
              onClick={handleToggleActive}
              disabled={toggling || submitting || !canModifyPolicy}
              title={!canModifyPolicy ? NOT_AUTHORISED_MSG : undefined}
              data-testid="BTN_policy_toggle_active"
            >
              {toggling
                ? "..."
                : selectedPolicy?.is_active
                  ? "Inactive"
                  : "Active"}
            </Button>
            <div style={{ display: "flex", gap: 8 }}>
              {!isEditMode ? (
                <Button
                  variant="fill"
                  icon={Edit2}
                  onClick={() => setIsEditMode(true)}
                  disabled={!canModifyPolicy}
                  title={!canModifyPolicy ? NOT_AUTHORISED_MSG : undefined}
                  data-testid="BTN_policy_edit"
                >
                  Edit
                </Button>
              ) : (
                <>
                  {selectedPolicy?.workflow_status === "draft" && (
                    <Button
                      variant="secondary-outline"
                      onClick={handleSubmitForApproval}
                      disabled={submitting || toggling || !canModifyPolicy}
                      title={!canModifyPolicy ? NOT_AUTHORISED_MSG : undefined}
                      data-testid="BTN_policy_submit_for_approval"
                    >
                      Submit for Approval
                    </Button>
                  )}
                  <Button
                    type="submit"
                    form="policy-form"
                    variant="update"
                    icon={Check}
                    disabled={submitting || toggling || !canModifyPolicy}
                    title={!canModifyPolicy ? NOT_AUTHORISED_MSG : undefined}
                    data-testid="BTN_policy_update"
                  >
                    {submitting ? "Updating..." : "Update"}
                  </Button>
                </>
              )}
            </div>
          </Modal.Footer>
        ) : !selectedPolicy ? (
          <Modal.Footer>
            <Button
              type="submit"
              form="policy-form"
              variant="fill"
              icon={Save}
              disabled={submitting || !canModifyPolicy}
              title={!canModifyPolicy ? NOT_AUTHORISED_MSG : undefined}
              data-testid="BTN_policy_submit"
            >
              {submitting ? "Submitting..." : "Submit"}
            </Button>
          </Modal.Footer>
        ) : null}
      </Modal>

      {/* ── Link SOP modal ── */}
      <Modal
        isOpen={showLinkPanel}
        onClose={() => {
          setShowLinkPanel(false);
          setLinkSearch("");
        }}
        size="sm"
      >
        <Modal.Header>
          Link SOP{selectedPolicy ? ` — ${selectedPolicy.policy_name}` : ""}
        </Modal.Header>
        <Modal.Body>
          <FormControl.SearchSelect
            label="Select SOP"
            placeholder={
              availableProcedures.length === 0
                ? "No SOPs available to link"
                : "Search and select an SOP…"
            }
            options={availableProcedures.map((proc) => ({
              value: proc.id,
              label: proc.description
                ? `${proc.process_name} — ${proc.description}`
                : proc.process_name,
            }))}
            value={null}
            onChange={async (val) => {
              if (val == null) return;
              const proc = availableProcedures.find((p) => p.id === Number(val));
              if (!proc) return;
              await handleLinkProcedure(proc);
              setShowLinkPanel(false);
              setLinkSearch("");
            }}
            disabled={availableProcedures.length === 0}
            data-testid="SEL_policy_link_sop"
          />
          {availableProcedures.length === 0 && (
            <p
              style={{
                marginTop: "8px",
                fontSize: "12px",
                color: "#9ca3af",
                fontStyle: "italic",
              }}
            >
              Either all SOPs are already linked, or none exist for this entity
              yet. Create SOPs under Governance → SOPs first.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary-outline"
            icon={X}
            onClick={() => {
              setShowLinkPanel(false);
              setLinkSearch("");
            }}
            data-testid="BTN_policy_link_sop_close"
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── SOP profile overlay ── mounts the SOP list+modal but keeps the
          host list hidden; the inner Modal opens automatically for the
          selected SOP via initialSopId. ── */}
      {sopProfileId != null && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1500,
            pointerEvents: "none",
          }}
        >
          <div style={{ display: "none" }}>
            <GovernanceProcesses
              initialSopId={sopProfileId}
              onClose={() => setSopProfileId(null)}
            />
          </div>
        </div>
      )}

      {/* Policy workflow — opened from the status dot. Driven end-to-end by the
          unified /api/v1/wf engine (kind="gov_policy"); a DB trigger keeps the
          legacy workflow_status column in sync with workflow_state. */}
      <WorkflowModal
        isOpen={!!workflowPolicy}
        onClose={() => setWorkflowPolicy(null)}
        kind="gov_policy"
        id={workflowPolicy ? Number(workflowPolicy.id) : null}
        onChanged={loadPolicies}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
};

export default GovernancePoliciesModal;
